import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsApi } from '../api/reports';
import { toErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useStores } from '../auth/StoreContext';
import { Card, Kpi, PageHead } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { money, count, toISODate } from '../lib/format';
import { CHART_COLORS, PAYMENT_COLORS } from '../lib/charts';
import type { DailyReport } from '../types';

export function DashboardPage() {
  const { isSuperAdmin, user } = useAuth();
  const today = toISODate(new Date());

  return (
    <>
      <PageHead
        title="Dashboard"
        subtitle={`Today · ${new Date().toLocaleDateString('en-IN', {
          weekday: 'long',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })}`}
      />
      {isSuperAdmin ? (
        <SuperAdminDashboard date={today} />
      ) : (
        <StoreDashboard storeId={user?.storeId ?? null} date={today} />
      )}
    </>
  );
}

// --------------------------- Super admin ----------------------------------

function SuperAdminDashboard({ date }: { date: string }) {
  const q = useQuery({
    queryKey: ['reports', 'consolidated', date],
    queryFn: () => reportsApi.consolidated(date),
  });

  const topProductsQ = useQuery({
    queryKey: ['reports', 'product-wise', 'today', date],
    queryFn: () =>
      reportsApi.productWise({ from: date, to: date }),
  });

  if (q.isLoading) return <Loading label="Loading consolidated report…" />;
  if (q.isError)
    return <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />;
  if (!q.data) return <Empty title="No data for today" />;

  const { stores, total } = q.data;

  const salesByStore = stores.map((s) => ({
    name: s.storeName,
    sales: s.totalSales,
  }));

  const paymentSplit = [
    { name: 'Cash', value: total.cashSales, key: 'CASH' },
    { name: 'UPI', value: total.upiSales, key: 'UPI' },
    { name: 'Card', value: total.cardSales, key: 'CARD' },
    { name: 'Other', value: total.otherSales, key: 'OTHER' },
  ].filter((p) => p.value > 0);

  const topProducts = (topProductsQ.data ?? [])
    .slice()
    .sort((a, b) => b.quantitySold - a.quantitySold)
    .slice(0, 8)
    .map((p) => ({ name: p.productName, qty: p.quantitySold }));

  return (
    <div className="stack">
      <div className="kpi-grid">
        <Kpi label="Total Sales" value={money(total.totalSales)} accent />
        <Kpi label="Orders" value={count(total.orderCount)} />
        <Kpi label="Avg Order Value" value={money(total.averageOrderValue)} />
        <Kpi label="Net Sales" value={money(total.netSales)} />
        <Kpi label="Total GST" value={money(total.totalCgst + total.totalSgst)} />
        <Kpi
          label="Discounts"
          value={money(total.totalDiscount)}
          sub={`${count(total.cancelledCount)} cancelled · ${money(
            total.refundedAmount,
          )} refunded`}
        />
      </div>

      <div className="grid-2">
        <Card title="Sales by Store">
          <div className="card-pad">
            {salesByStore.length === 0 ? (
              <Empty title="No sales yet today" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={salesByStore} margin={{ top: 8, right: 8 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(v: number) => money(v)}
                    cursor={{ fill: 'rgba(228,0,43,0.06)' }}
                  />
                  <Bar dataKey="sales" radius={[6, 6, 0, 0]}>
                    {salesByStore.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card title="Payment Split">
          <div className="card-pad">
            {paymentSplit.length === 0 ? (
              <Empty title="No payments yet today" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={paymentSplit}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                  >
                    {paymentSplit.map((p) => (
                      <Cell
                        key={p.key}
                        fill={PAYMENT_COLORS[p.key] ?? '#8A827B'}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => money(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      <Card title="Top Products Today">
        <div className="card-pad">
          {topProductsQ.isLoading ? (
            <Loading />
          ) : topProducts.length === 0 ? (
            <Empty title="No products sold yet today" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={topProducts}
                layout="vertical"
                margin={{ left: 20, right: 16 }}
              >
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(v: number) => [`${v}`, 'Qty sold']}
                  cursor={{ fill: 'rgba(228,0,43,0.06)' }}
                />
                <Bar dataKey="qty" fill="#E4002B" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <Card title="Per-Store Breakdown">
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Store</th>
                <th className="num">Orders</th>
                <th className="num">Sales</th>
                <th className="num">Discount</th>
                <th className="num">GST</th>
                <th className="num">Net</th>
                <th className="num">Cancelled</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((s) => (
                <StoreRow key={s.storeName} row={s} />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{count(total.orderCount)}</td>
                <td className="num">{money(total.totalSales)}</td>
                <td className="num">{money(total.totalDiscount)}</td>
                <td className="num">
                  {money(total.totalCgst + total.totalSgst)}
                </td>
                <td className="num">{money(total.netSales)}</td>
                <td className="num">{count(total.cancelledCount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StoreRow({ row }: { row: DailyReport }) {
  return (
    <tr>
      <td>{row.storeName}</td>
      <td className="num">{count(row.orderCount)}</td>
      <td className="num">{money(row.totalSales)}</td>
      <td className="num">{money(row.totalDiscount)}</td>
      <td className="num">{money(row.totalCgst + row.totalSgst)}</td>
      <td className="num">{money(row.netSales)}</td>
      <td className="num">{count(row.cancelledCount)}</td>
    </tr>
  );
}

// --------------------------- Store admin ----------------------------------

function StoreDashboard({
  storeId,
  date,
}: {
  storeId: number | null;
  date: string;
}) {
  const { currentStore } = useStores();
  const [selectedDate, setSelectedDate] = useState(date);

  const q = useQuery({
    queryKey: ['reports', 'daily', storeId, selectedDate],
    queryFn: () => reportsApi.daily(storeId, selectedDate),
    enabled: storeId != null,
  });

  const paymentSplit = useMemo(() => {
    if (!q.data) return [];
    const d = q.data;
    return [
      { name: 'Cash', value: d.cashSales, key: 'CASH' },
      { name: 'UPI', value: d.upiSales, key: 'UPI' },
      { name: 'Card', value: d.cardSales, key: 'CARD' },
      { name: 'Other', value: d.otherSales, key: 'OTHER' },
    ].filter((p) => p.value > 0);
  }, [q.data]);

  if (storeId == null)
    return <ErrorState message="No store is associated with your account." />;

  return (
    <div className="stack">
      <div className="toolbar">
        <span className="badge badge-red">{currentStore?.name ?? 'Store'}</span>
        <div className="spacer" />
        <label style={{ margin: 0 }}>Date</label>
        <input
          type="date"
          style={{ width: 'auto' }}
          value={selectedDate}
          max={date}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
      </div>

      {q.isLoading ? (
        <Loading label="Loading daily report…" />
      ) : q.isError ? (
        <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
      ) : !q.data ? (
        <Empty title="No data for this date" />
      ) : (
        <>
          <div className="kpi-grid">
            <Kpi label="Total Sales" value={money(q.data.totalSales)} accent />
            <Kpi label="Orders" value={count(q.data.orderCount)} />
            <Kpi
              label="Avg Order Value"
              value={money(q.data.averageOrderValue)}
            />
            <Kpi label="Net Sales" value={money(q.data.netSales)} />
            <Kpi
              label="GST Collected"
              value={money(q.data.totalCgst + q.data.totalSgst)}
              sub={`CGST ${money(q.data.totalCgst)} · SGST ${money(
                q.data.totalSgst,
              )}`}
            />
            <Kpi
              label="Cancelled / Refunded"
              value={count(q.data.cancelledCount)}
              sub={`${money(q.data.refundedAmount)} refunded`}
            />
          </div>

          <div className="grid-2">
            <Card title="Payment Split">
              <div className="card-pad">
                {paymentSplit.length === 0 ? (
                  <Empty title="No payments on this date" />
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={paymentSplit}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={60}
                        outerRadius={95}
                        paddingAngle={2}
                      >
                        {paymentSplit.map((p) => (
                          <Cell
                            key={p.key}
                            fill={PAYMENT_COLORS[p.key] ?? '#8A827B'}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => money(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>

            <Card title="Sales Composition">
              <div className="card-pad">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={[
                      { name: 'Gross', value: q.data.totalSales },
                      { name: 'Discount', value: q.data.totalDiscount },
                      {
                        name: 'GST',
                        value: q.data.totalCgst + q.data.totalSgst,
                      },
                      { name: 'Net', value: q.data.netSales },
                    ]}
                  >
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => money(v)} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {CHART_COLORS.slice(0, 4).map((c, i) => (
                        <Cell key={i} fill={c} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
